import * as React from "react";
import {
    Box, Card, CardContent, CardHeader, TextField, Button,
    Typography, Alert, CircularProgress, Snackbar
} from "@mui/material";
import { registerNewRelicKey } from "../../utils/newRelicIntegrationUtil";
import { useAuth } from "../../auth/AuthContext";

/**
 * Screen for securely submitting a user's New Relic key.
 * - The key is posted in the request body over HTTPS and is never echoed.
 * - The backend associates the key to the caller's MSAL OID from the JWT.
 */
const IS_MOCK_MODE = process.env.REACT_APP_AUTH_MODE !== "ms_entra_id"; // per project convention

export default function NewRelicIntegration() {
    const { account, getAccessToken } = useAuth();
    const [key, setKey] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [okOpen, setOkOpen] = React.useState(false);

    const isAuthenticated = Boolean(account?.oid);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);

        const trimmed = key.trim();
        if (!trimmed) {
            setError("Please enter your New Relic key.");
            return;
        }

        setSubmitting(true);
        try {
            const token = IS_MOCK_MODE ? undefined : await getAccessToken!();
            await registerNewRelicKey({ key: trimmed, accessToken: token ?? undefined });
            setKey("");            // clear sensitive input from memory
            setOkOpen(true);
        } catch (err: any) {
            setError(err?.message ?? "Failed to save key.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Box sx={{ maxWidth: 640, mx: "auto", mt: 6, px: 2 }}>
            <Card elevation={3}>
                <CardHeader
                    title="Connect New Relic"
                    subheader="Store your New Relic key securely so the app can fetch your data on your behalf."
                />
                <CardContent>
                    {IS_MOCK_MODE && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Mock mode is active. The key will not be sent or stored; success is simulated.
                        </Alert>
                    )}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            This app requires a <strong>New Relic User API Key</strong>.
                            You can generate one in New Relic under
                            <strong> Account settings &gt; API keys &gt; Create a Key</strong>.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            User keys typically begin with <strong>NRAK</strong>.
                        </Typography>
                    </Alert>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box component="form" onSubmit={onSubmit} autoComplete="off" noValidate>
                        <TextField
                            label="New Relic Key"
                            type="password"
                            fullWidth
                            required
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            inputProps={{ autoComplete: "off", spellCheck: "false" }}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || (!IS_MOCK_MODE && !isAuthenticated)}
                            startIcon={submitting ? <CircularProgress size={18} /> : null}
                        >
                            {submitting ? "Saving…" : "Save Key"}
                        </Button>
                    </Box>

                    {!IS_MOCK_MODE && (
                        <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
                            The key is transmitted over HTTPS and stored encrypted server-side. It is not logged or shown back to you.
                        </Typography>
                    )}
                </CardContent>
            </Card>

            <Snackbar
                open={okOpen}
                autoHideDuration={3000}
                onClose={() => setOkOpen(false)}
                message={IS_MOCK_MODE ? "Simulated: key saved." : "New Relic key saved."}
            />
        </Box>
    );
}
